import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Server, Activity, ShieldAlert, ArrowLeft } from 'lucide-react';
import { useLive } from '@/lib/live';
import { LiveDot } from '@/components/enterprise/ui';

const MODULES = [
  { to: '/plattform', label: 'Übersicht', icon: LayoutDashboard, exact: true },
  { to: '/plattform/proxmox', label: 'Proxmox Ops', icon: Server },
  { to: '/plattform/monitoring', label: 'Monitoring', icon: Activity },
  { to: '/plattform/security', label: 'Security SOC', icon: ShieldAlert },
];

function Clock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setT(new Date()), 1000); return () => clearInterval(i); }, []);
  return <span className="font-mono text-[11px] text-[color:var(--itw-text-3)] tabular-nums hidden sm:inline">{t.toLocaleTimeString('de-DE')} UTC{-t.getTimezoneOffset() / 60 >= 0 ? '+' : ''}{-t.getTimezoneOffset() / 60}</span>;
}

export default function DashboardShell({ title, subtitle, children }) {
  const { pathname } = useLocation();
  const live = useLive();
  const online = live?.status === 'online';
  const isActive = (m) => (m.exact ? pathname === m.to : pathname.startsWith(m.to));

  return (
    <div className="min-h-screen bg-[color:var(--itw-bg)] text-[color:var(--itw-text)] flex">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-[color:var(--itw-border)] bg-[color:var(--itw-surface)]/60 sticky top-0 h-screen">
        <div className="px-4 py-4 border-b border-[color:var(--itw-border)]">
          <Link to="/" className="font-display font-semibold text-sm">IT-WELT<span className="text-[color:var(--itw-cyan)]">360</span></Link>
          <div className="mt-0.5 text-[10px] uppercase tracking-widest text-[color:var(--itw-text-3)] font-mono">Operations Platform</div>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {MODULES.map((m) => (
            <Link key={m.to} to={m.to} data-testid={`platform-nav-${m.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-[background-color,color] duration-200 ${isActive(m) ? 'bg-[color:var(--itw-cyan)]/12 text-[color:var(--itw-cyan)]' : 'text-[color:var(--itw-text-2)] hover:bg-[color:var(--itw-surface-2)] hover:text-[color:var(--itw-text)]'}`}>
              <m.icon className="h-4 w-4" /> {m.label}
            </Link>
          ))}
        </nav>
        <div className="p-3 border-t border-[color:var(--itw-border)]">
          <Link to="/" className="flex items-center gap-1.5 text-xs text-[color:var(--itw-text-3)] hover:text-[color:var(--itw-cyan)] transition-colors"><ArrowLeft className="h-3.5 w-3.5" /> Zur Website</Link>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-[color:var(--itw-border)] bg-[color:var(--itw-bg)]/85 backdrop-blur-md">
          <div className="min-w-0">
            <h1 className="text-base font-semibold truncate">{title}</h1>
            {subtitle && <p className="text-[11px] text-[color:var(--itw-text-3)] font-mono truncate">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3"><LiveDot on={online} /><Clock /></div>
        </header>
        <div className="md:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b border-[color:var(--itw-border)]">
          {MODULES.map((m) => (
            <Link key={m.to} to={m.to} className={`shrink-0 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs ${isActive(m) ? 'bg-[color:var(--itw-cyan)]/12 text-[color:var(--itw-cyan)]' : 'text-[color:var(--itw-text-2)]'}`}>
              <m.icon className="h-3.5 w-3.5" /> {m.label}
            </Link>
          ))}
        </div>
        <main className="flex-1 p-4 sm:p-6 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
